export const isElectron = () =>
  typeof window !== 'undefined' && window.process && window.process.type;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const money = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const printHtmlInIframe = (html, title = 'Print') => {
  if (typeof document === 'undefined') return;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  // Viewport = half A4 (same as bill box). Page itself is portrait so printers
  // don't rotate the content 90°.
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '210mm';
  iframe.style.height = '148mm';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 1000);
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    cleanup();
    return;
  }

  let hasPrinted = false;
  const triggerPrint = () => {
    if (hasPrinted) return;
    hasPrinted = true;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (err) {
      console.error('Web print failed:', err);
    } finally {
      cleanup();
    }
  };

  iframe.onload = () => setTimeout(triggerPrint, 300);

  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(
      title
    )}</title></head><body>${html}</body></html>`
  );
  doc.close();

  setTimeout(triggerPrint, 600);
};

// Same wording style as SalesEntry numToWords / generatePDF
const numberToWords = (num) => {
  const value = Number(num);
  if (!Number.isFinite(value) || value === 0) return 'Zero Rupees Only';

  const units = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (n) => {
    if (n < 20) return units[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
    if (n < 1000) return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  return convert(Math.floor(value)) + ' Rupees Only';
};

// Same visual layout as generatePDF, printed upright on half of A4.
export const buildA5ReceiptHtml = (data, options = {}) => {
  const { title = 'PHARMACY CASH BILL' } = options;
  const header = data?.header || {};
  const totals = data?.totals || {};
  const settings = data?.settings || {};
  const products = Array.isArray(data?.products) ? data.products : [];

  const phones = Array.isArray(header.phones) ? header.phones.filter(Boolean) : [];
  const address = Array.isArray(header.address)
    ? header.address.filter(Boolean).join(', ')
    : String(header.address || '');

  const firmName = String(header.firmName || '').toUpperCase();
  const returnDays = settings.returnValidityDays || 7;
  const policy = `Return accepted within ${returnDays} days for unopened medicines along with the purchase receipt`;
  const refrigeratedNote = 'Refrigerator keeping medicines will accept return';

  // Web-only: hide GST columns / Total GST / GST No when phIsGstBill is not "Yes".
  // Reads from the payload settings already sent for print (does not change Electron).
  const isGstBill =
    String(
      settings.is_gstbill ??
        settings.phIsGstBill ??
        options.phIsGstBill ??
        'No'
    ).toLowerCase() === 'yes';

  // generalSettings.ShowItemwiseDiscount — the per-item Disc column only shows
  // when this is "Yes". The Total Discount line in the totals block always shows.
  const showItemDiscount =
    String(
      settings.show_itemwise_discount ??
        settings.ShowItemwiseDiscount ??
        options.showItemwiseDiscount ??
        'No'
    ).toLowerCase() === 'yes';

  const ITEMS_PER_PAGE = 13;
  const validProducts = products.filter((p) => p && p.name);
  const pageCount = Math.max(1, Math.ceil(validProducts.length / ITEMS_PER_PAGE));

  const subTotal = Number(totals.totalAmount) || Number(totals.grandTotal) || 0;
  const totalDiscount = Number(totals.discount) || 0;
  const totalGst = Number(totals.gst) || 0;
  const netTotal = Number(totals.grandTotal) || subTotal;

  // Product Name absorbs the width freed by any hidden optional column.
  const nameWidth =
    100 - (4 + 10 + 6 + 8 + 10 + 12) - (showItemDiscount ? 9 : 0) - (isGstBill ? 17 : 0);

  const tableHead = `
    <tr>
      <th class="c" style="width:4%">Sr</th>
      <th class="l" style="width:${nameWidth}%">Product Name</th>
      <th class="l" style="width:10%">Batch</th>
      <th class="c" style="width:6%">Qty</th>
      <th class="c" style="width:8%">Exp</th>
      <th class="r" style="width:10%">Mrp</th>
      ${showItemDiscount ? '<th class="r" style="width:9%">Disc</th>' : ''}
      ${
        isGstBill
          ? `<th class="r" style="width:8%">GST%</th>
      <th class="r" style="width:9%">GST</th>`
          : ''
      }
      <th class="r" style="width:12%">Total</th>
    </tr>`;

  const buildRows = (pageProducts, startIndex) =>
    pageProducts
      .map((p, index) => {
        const sr = p.sr ?? startIndex + index + 1;
        return `
      <tr>
        <td class="c">${escapeHtml(sr)}</td>
        <td class="l">${escapeHtml(p.name)}</td>
        <td class="l">${escapeHtml(p.batch || '')}</td>
        <td class="c">${escapeHtml(p.qty ?? '')}</td>
        <td class="c">${escapeHtml(p.exp || '')}</td>
        <td class="r">${money(p.mrp ?? p.grandTotal)}</td>
        ${showItemDiscount ? `<td class="r">${money(p.discount)}</td>` : ''}
        ${
          isGstBill
            ? `<td class="r">${p.gstRate != null && p.gstRate !== '' ? `${escapeHtml(p.gstRate)}%` : '0%'}</td>
        <td class="r">${money(p.gstAmount)}</td>`
            : ''
        }
        <td class="r">${money(p.total)}</td>
      </tr>`;
      })
      .join('');

  const buildPage = (pageIndex) => {
    const start = pageIndex * ITEMS_PER_PAGE;
    const pageProducts = validProducts.slice(start, start + ITEMS_PER_PAGE);
    const pageLabel =
      pageCount > 1 ? `<p class="page-no">Page ${pageIndex + 1} of ${pageCount}</p>` : '';

    return `
  <div class="a5-bill">
    <div class="head">
      <p class="firm">${escapeHtml(firmName)}</p>
      ${address ? `<p>${escapeHtml(address)}</p>` : ''}
      ${phones.length ? `<p>Phone No : ${escapeHtml(phones.join(', '))}</p>` : ''}
      ${isGstBill && header.gst ? `<p>GST No : ${escapeHtml(header.gst)}</p>` : ''}
      ${
        header.dlnum1 || header.dlnum2
          ? `<p>DL No : ${escapeHtml([header.dlnum1, header.dlnum2].filter(Boolean).join(', '))}</p>`
          : ''
      }
    </div>

    <p class="title">${escapeHtml(title)}</p>

    <div class="meta">
      <div class="left">
        <p>Patient : ${escapeHtml(header.patientName || '')}</p>
        <p>Doctor : ${escapeHtml(header.doctor || '')}</p>
      </div>
      <div class="right">
        <p>Invoice No : ${escapeHtml(header.invoiceNumber || '')}</p>
        <p>Date : ${escapeHtml(header.date || '')}</p>
        ${pageLabel}
      </div>
    </div>

    <div class="rule"></div>

    <div class="table-wrap">
      <table>
        <thead>${tableHead}</thead>
        <tbody>
          ${buildRows(pageProducts, start)}
        </tbody>
      </table>
    </div>

    <div class="foot">
      <div class="rule"></div>
      <div class="footer">
        <div class="left">
          <p class="saved">You have saved : ${money(totalDiscount)}</p>
          <p>Amount to Pay : ${escapeHtml(numberToWords(netTotal))}</p>
          <p>Printed By : ${escapeHtml(header.username || '')}</p>
        </div>
        <div class="totals">
          <div class="labels">
            <p>Grand Total</p>
            <p>Total Discount</p>
            ${isGstBill ? '<p>Total GST</p>' : ''}
            <p class="net">Net Total</p>
          </div>
          <div class="values">
            <p>${money(subTotal)}</p>
            <p>${money(totalDiscount)}</p>
            ${isGstBill ? `<p>${money(totalGst)}</p>` : ''}
            <p class="net">${money(netTotal)}</p>
          </div>
        </div>
      </div>
      <p class="policy">${escapeHtml(policy)}</p>
      <p class="policy">${escapeHtml(refrigeratedNote)}</p>
    </div>
  </div>`;
  };

  const onePassHtml = Array.from({ length: pageCount }, (_, i) => buildPage(i)).join('');

  // generalSettings.PhBillsQty — repeat the whole bill for each copy.
  const copies = Math.max(1, Math.min(Number(options.copies) || 1, 10));
  const pagesHtml = Array.from({ length: copies }, () => onePassHtml).join('');

  return `
  <style>
    /*
      CRITICAL: use PORTRAIT page so the printer does not rotate the sheet.
      Bill box is exactly half A4 (210mm × 148mm) — same as A5 landscape PDF.
      Max 13 items per page; overflow continues on the next sheet.
    */
    @page {
      size: A4 portrait;
      margin: 0;
    }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      width: 210mm;
      background: #fff;
    }
    .a5-bill {
      font-family: Helvetica, Arial, sans-serif;
      color: #000;
      width: 210mm;
      height: 148mm; /* half of A4 — same footprint as your PDF */
      overflow: hidden;
      display: flex;
      flex-direction: column;
      padding: 8mm 10mm 6mm;
      background: #fff;
      page-break-after: always;
      break-after: page;
    }
    .a5-bill:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .a5-bill .head { text-align: center; }
    .a5-bill .firm {
      font-size: 16px;
      font-weight: 700;
      margin: 0 0 3px;
    }
    .a5-bill .head p {
      margin: 0 0 2px;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.25;
    }
    .a5-bill .title {
      text-align: center;
      font-size: 14px;
      font-weight: 700;
      margin: 8px 0 10px;
    }
    .a5-bill .meta {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      margin-bottom: 4px;
    }
    .a5-bill .meta p { margin: 0 0 3px; line-height: 1.3; }
    .a5-bill .meta .right { text-align: right; }
    .a5-bill .page-no { font-size: 10px; margin: 2px 0 0; }
    .a5-bill .rule { width: 100%; height: 1px; background: #000; flex-shrink: 0; }
    .a5-bill .table-wrap { flex: 1 1 auto; min-height: 0; overflow: hidden; }
    .a5-bill table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin-top: 3px;
      table-layout: fixed;
    }
    .a5-bill thead th {
      font-weight: 700;
      padding: 3px 2px;
      border-bottom: 1px solid #000;
    }
    .a5-bill tbody td {
      padding: 2px;
      vertical-align: top;
      word-wrap: break-word;
    }
    .a5-bill .c { text-align: center; }
    .a5-bill .l { text-align: left; }
    .a5-bill .r { text-align: right; }
    .a5-bill .foot { margin-top: auto; flex-shrink: 0; }
    .a5-bill .footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 6px 0 3px;
      font-size: 12px;
      gap: 16px;
    }
    .a5-bill .footer .left { flex: 1; min-width: 0; }
    .a5-bill .footer .left p { margin: 0 0 5px; }
    .a5-bill .footer .saved { font-weight: 700; }
    .a5-bill .totals {
      display: flex;
      gap: 20px;
      flex-shrink: 0;
    }
    .a5-bill .totals .labels,
    .a5-bill .totals .values { text-align: right; white-space: nowrap; }
    .a5-bill .totals p { margin: 0 0 5px; }
    .a5-bill .totals .net { font-weight: 700; }
    .a5-bill .policy {
      text-align: center;
      font-size: 9px;
      margin: 4px 0 0;
    }
    .a5-bill .policy + .policy { margin-top: 1px; }
  </style>
  ${pagesHtml}`;
};

export const webPrintA5Receipt = (data, options = {}) => {
  const html = buildA5ReceiptHtml(data, options);
  printHtmlInIframe(html, options.title || 'Bill');
};

export const webPrintElementById = (elementId = 'billDiv', options = {}) => {
  if (typeof document === 'undefined') return;
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`webPrintElementById: element #${elementId} not found`);
    return;
  }

  const styleTags = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]')
  )
    .map((node) => node.outerHTML)
    .join('\n');

  const html = `
    <style>
      @page { size: A4 portrait; margin: 0; }
      body { margin: 0; }
      #${elementId} {
        width: 210mm !important;
        height: 148mm !important;
      }
    </style>
    ${styleTags}
    ${element.outerHTML}`;

  printHtmlInIframe(html, options.title || 'Bill');
};
